import { useMemo, useState } from 'react';
import { DatesSetArg, FormatterInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { DateTime } from 'luxon';
import { Select, Typography } from '@la/ds-ui-components';
import { getLAHostnameParts } from '@la/utilities';
import LoadingIndicatorOverlay from 'components/LoadingIndicatorOverlay/LoadingIndicatorOverlay';
import {
  useGetSiteActivitiesPostQuery,
  useGetSiteProgramsQuery,
} from 'redux/services/siteLevelCalendarApi';
import { Program } from 'domains/ActivityForm/openapi-client/models';
import MainContent from 'domains/Shell/MainContent/MainContent';
import styles from './SiteLevelCalendar.module.scss';
import SiteLevelCalendarEventContent from './SiteLevelCalendarEventContent';
import './fullCalendar.css';

const DATE_FORMAT: FormatterInput = {
  hour: 'numeric',
  minute: '2-digit',
  omitZeroMinute: true,
  meridiem: 'short',
};

export const activityTypes = {
  game_consolation: 'Game',
  game_league: 'Game',
  game_friendly: 'Game',
  game_pool_play: 'Game',
  game_playoff: 'Game',
  game_scrimmage: 'Game',
  game_season: 'Game',
  game_tournament: 'Game',
  game_travel: 'Game',
  game_final: 'Game',
  event_practice: 'Practice',
  event_lesson: 'Lesson',
  event_training: 'Training',
  event_meeting: 'Meeting',
  event_tournament: 'Tournament',
  event_league: 'League',
  event_camp: 'Camp',
  event_class: 'Class',
  event_other: 'Other',
};

const DEFAULT_SELECTED_PROGRAM = 'All Programs';

export default function SiteLevelCalendar() {
  const { subdomain } = getLAHostnameParts();

  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>();

  const { data: activities, isLoading } = useGetSiteActivitiesPostQuery(
    {
      requestData: {
        parentProgramIds: selectedProgram ? [Number(selectedProgram)] : [],
        startDateFrom: selectedDate?.split(',')[0],
        startDateUpTo: selectedDate?.split(',')[1],
      },
      subdomain,
    },
    {
      skip: !selectedDate,
    }
  );

  const { data: programs, isLoading: isProgramsLoading } =
    useGetSiteProgramsQuery({ subdomain });

  const filteredPrograms = programs?.filter(
    (program: Program) =>
      program.state === 'LIVE' || program.state === 'UPCOMING'
  );

  const programOptions = useMemo(() => {
    const defaultProgramOption = {
      value: '',
      label: DEFAULT_SELECTED_PROGRAM,
    };

    if (!filteredPrograms || filteredPrograms.length === 0) {
      return [defaultProgramOption];
    }

    const sortedProgramOptions = filteredPrograms.map((program: Program) => {
      let label = program.isSub
        ? '\u00A0\u00A0\u00A0\u00A0' + program.name
        : program.fullName;
      return program.fullName
        ? {
            value: program.id,
            label: label,
          }
        : null;
    });

    return [defaultProgramOption, ...sortedProgramOptions];
  }, [filteredPrograms]);

  const handleDateChange = (date: DatesSetArg) => {
    const { start, end } = date;
    const formattedStartDate =
      DateTime.fromJSDate(start).toFormat('yyyy-MM-dd');
    const formattedEndDate = DateTime.fromJSDate(end).toFormat('yyyy-MM-dd');

    setSelectedDate(`${formattedStartDate},${formattedEndDate}`);
  };

  return (
    <MainContent>
      <div>
        <h1>Site Level Calendar</h1>
        <div>
          <Typography variant="ui" size="medium">
            Choose a program to view its activities
          </Typography>
          <Select
            hidePlaceholder
            placeholder="Select an option..."
            id="program-picker"
            options={programOptions}
            value={selectedProgram}
            onChange={setSelectedProgram}
          />
        </div>
        <div className={styles.siteCalendarContainer}>
          <div className={styles.siteCalendar} id="siteCalendar">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next,today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              dayMaxEvents={true}
              datesSet={handleDateChange}
              editable={false}
              selectable={true}
              selectMirror={true}
              weekends={true}
              events={activities}
              eventContent={(content) => (
                <SiteLevelCalendarEventContent
                  content={content}
                  selectedColorFilter="program"
                />
              )}
              eventTimeFormat={DATE_FORMAT}
              displayEventEnd={false}
            />
          </div>
        </div>
      </div>
      {(isLoading || isProgramsLoading) && (
        <LoadingIndicatorOverlay message="Loading..." />
      )}
    </MainContent>
  );
}
